import React, { useState, useEffect } from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { CheckCircle, Award, Star, Shield, TrendingUp, Clock, Gift } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Badge color mapping
const BADGE_COLORS = {
  amber: 'bg-amber-100 text-amber-800 border-amber-300',
  emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  gold: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  blue: 'bg-blue-100 text-blue-800 border-blue-300',
  purple: 'bg-purple-100 text-purple-800 border-purple-300',
  green: 'bg-green-100 text-green-800 border-green-300',
  indigo: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  gray: 'bg-gray-100 text-gray-800 border-gray-300',
};

// Individual Badge Display Component
export const CandidateBadge = ({ badge, size = 'default', showTooltip = true }) => {
  const colorClass = BADGE_COLORS[badge.color] || BADGE_COLORS.gray;
  
  const sizeClasses = {
    small: 'text-xs px-2 py-0.5',
    default: 'text-sm px-3 py-1',
    large: 'text-base px-4 py-2',
  };

  return (
    <div className="relative group inline-block" data-testid={`badge-${badge.id || badge.badge_id}`}>
      <Badge 
        variant="outline" 
        className={`${colorClass} ${sizeClasses[size]} border font-medium flex items-center gap-1.5 cursor-default`}
      >
        <span>{badge.icon}</span>
        <span>{badge.name}</span>
      </Badge>
      
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none">
          {badge.description}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

// Badge Row for Profile Headers
export const CandidateBadgeRow = ({ badges, maxDisplay = 3 }) => {
  if (!badges || badges.length === 0) return null;
  
  const displayBadges = badges.slice(0, maxDisplay);
  const remaining = badges.length - maxDisplay;

  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="candidate-badge-row">
      {displayBadges.map((badge) => (
        <CandidateBadge key={badge.badge_id || badge.id} badge={badge} size="small" />
      ))}
      {remaining > 0 && (
        <Badge variant="secondary" className="text-xs">
          +{remaining} more
        </Badge>
      )}
    </div>
  );
};

// Badge Progress Card (for badge application flow)
export const BadgeProgressCard = ({ badge, candidateEmail, onApply }) => {
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    checkEligibility();
  }, [badge.id, candidateEmail]);

  const checkEligibility = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/candidate-badges/check-eligibility/${badge.id}?candidate_email=${encodeURIComponent(candidateEmail)}`
      );
      const data = await response.json();
      setEligibility(data);
    } catch (error) {
      console.error('Error checking eligibility:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const response = await fetch(`${API_URL}/api/candidate-badges/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          badge_id: badge.id,
          candidate_email: candidateEmail,
          services_completed: eligibility?.completed_services || [],
        }),
      });
      const data = await response.json();
      if (data.success) {
        onApply?.(data.badge);
        checkEligibility(); // Refresh
      }
    } catch (error) {
      console.error('Error applying for badge:', error);
    } finally {
      setApplying(false);
    }
  };

  const colorClass = BADGE_COLORS[badge.color] || BADGE_COLORS.gray;

  return (
    <Card className="relative overflow-hidden" data-testid={`badge-progress-${badge.id}`}>
      <div className={`absolute top-0 left-0 w-1 h-full ${colorClass.split(' ')[0]}`} />
      
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${colorClass}`}>
              {badge.icon}
            </div>
            <div>
              <CardTitle className="text-lg">{badge.name}</CardTitle>
              <CardDescription>{badge.description}</CardDescription>
            </div>
          </div>
          {eligibility?.eligible === false && eligibility?.reason === 'already_awarded' && (
            <Badge className="bg-green-500 text-white">
              <CheckCircle className="w-3 h-3 mr-1" />
              Earned
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="animate-pulse h-20 bg-gray-100 rounded" />
        ) : eligibility?.eligible === false && eligibility?.reason === 'already_awarded' ? (
          <div className="text-sm text-green-600 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            You've already earned this badge!
          </div>
        ) : eligibility?.eligible ? (
          <div className="space-y-3">
            <div className="text-sm text-emerald-600 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              All requirements met!
            </div>
            <Button 
              onClick={handleApply} 
              disabled={applying}
              className="w-full"
              data-testid={`apply-badge-${badge.id}`}
            >
              {applying ? 'Applying...' : `Claim ${badge.name} Badge`}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Progress</span>
              <span className="font-medium">{eligibility?.progress?.percentage || 0}%</span>
            </div>
            <Progress value={eligibility?.progress?.percentage || 0} className="h-2" />
            
            {eligibility?.missing_services?.length > 0 && (
              <div className="mt-3">
                <p className="text-sm text-gray-600 mb-2">Complete these services:</p>
                <ul className="text-sm space-y-1">
                  {eligibility.missing_services.map((service) => (
                    <li key={service} className="flex items-center gap-2 text-amber-600">
                      <Clock className="w-3 h-3" />
                      {service.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        
        {/* Benefits */}
        {badge.benefits && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
              <Gift className="w-3 h-3" />
              Badge Benefits
            </p>
            <ul className="text-xs text-gray-600 space-y-1">
              {badge.benefits.slice(0, 3).map((benefit, idx) => (
                <li key={idx} className="flex items-start gap-1">
                  <Star className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Search Boost */}
        {badge.search_boost > 1 && (
          <div className="mt-3 flex items-center gap-2 text-xs text-emerald-600">
            <TrendingUp className="w-3 h-3" />
            {Math.round((badge.search_boost - 1) * 100)}% search visibility boost
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Full Badge Dashboard for Candidates
export const CandidateBadgeDashboard = ({ candidateEmail, category }) => {
  const [badges, setBadges] = useState([]);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [candidateEmail, category]);

  const fetchData = async () => {
    try {
      // Fetch available badges for category
      const badgesRes = await fetch(`${API_URL}/api/candidate-badges/category/${category}`);
      const badgesData = await badgesRes.json();
      setBadges(badgesData.badges || []);

      // Fetch earned badges
      if (candidateEmail) {
        const earnedRes = await fetch(
          `${API_URL}/api/candidate-badges/candidate/${encodeURIComponent(candidateEmail)}`
        );
        const earnedData = await earnedRes.json();
        setEarnedBadges(earnedData.badges || []);
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBadgeEarned = (badge) => {
    setEarnedBadges(prev => [...prev, badge]);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse h-40 bg-gray-100 rounded-lg" />
        ))}
      </div>
    );
  }

  const earnedBadgeIds = earnedBadges.map(b => b.badge_id);

  return (
    <div className="space-y-6" data-testid="candidate-badge-dashboard">
      {/* Earned Badges Section */}
      {earnedBadges.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Your Badges ({earnedBadges.length})
          </h3>
          <div className="flex flex-wrap gap-3">
            {earnedBadges.map((badge) => (
              <CandidateBadge key={badge.badge_id} badge={badge} size="large" />
            ))}
          </div>
        </div>
      )}

      {/* Available Badges Section */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          Available Badges
        </h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {badges
            .filter(b => !earnedBadgeIds.includes(b.id))
            .map((badge) => (
              <BadgeProgressCard
                key={badge.id}
                badge={badge}
                candidateEmail={candidateEmail}
                onApply={handleBadgeEarned}
              />
            ))}
        </div>
        
        {badges.filter(b => !earnedBadgeIds.includes(b.id)).length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>You've earned all available badges for this category!</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Employer-facing badge display (for viewing candidate profiles)
export const CandidateBadgeDisplay = ({ candidateEmail }) => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (candidateEmail) {
      fetchBadges();
    }
  }, [candidateEmail]);

  const fetchBadges = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/candidate-badges/candidate/${encodeURIComponent(candidateEmail)}`
      );
      const data = await response.json();
      setBadges(data.badges || []);
    } catch (error) {
      console.error('Error fetching candidate badges:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse h-8 w-40 bg-gray-100 rounded" />;
  }

  if (badges.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="candidate-badge-display">
      <p className="text-xs text-gray-500 font-medium">Verified Skills</p>
      <CandidateBadgeRow badges={badges} maxDisplay={4} />
    </div>
  );
};

export default CandidateBadgeDashboard;
